export const STAGE_KEYS = [
  'saved',
  'applied',
  'screening',
  'screening_wait',
  'interview',
  'interview_wait',
  'final',
  'final_wait',
  'offer',
  'rejected',
  'ghosted',
] as const;
export type Stage = (typeof STAGE_KEYS)[number];

export interface StageInfo {
  key: Stage;
  title: string;
  closed: boolean;
}

export const STAGES: readonly StageInfo[] = [
  { key: 'saved', title: 'Интересно', closed: false },
  { key: 'applied', title: 'Отклик', closed: false },
  { key: 'screening', title: 'HR-скрининг', closed: false },
  { key: 'screening_wait', title: 'Ждём итогов: HR-скрининг', closed: false },
  { key: 'interview', title: 'Техническое интервью', closed: false },
  { key: 'interview_wait', title: 'Ждём итогов: техинтервью', closed: false },
  { key: 'final', title: 'Финальное интервью', closed: false },
  { key: 'final_wait', title: 'Ждём итогов: финал', closed: false },
  { key: 'offer', title: 'Оффер', closed: false },
  { key: 'rejected', title: 'Отказ', closed: true },
  { key: 'ghosted', title: 'Нет ответа', closed: true },
];

/**
 * Прежние статусы (данные прототипа, импорт, старые ответы Claude) → текущие:
 * «Связались» → HR-скрининг, «Принят» → Оффер, «Отозвал» → Отказ.
 */
export const LEGACY_STAGES: Readonly<Record<string, Stage>> = {
  contact: 'screening',
  accepted: 'offer',
  withdrawn: 'rejected',
};

/** Текущий статус из строки (с учётом прежних названий); не статус — null. */
export function normStage(v: unknown): Stage | null {
  if (typeof v !== 'string') return null;
  return (STAGE_KEYS as readonly string[]).includes(v) ? (v as Stage) : (LEGACY_STAGES[v] ?? null);
}

const BY_KEY = new Map(STAGES.map((s) => [s.key, s]));

export function isStage(v: unknown): v is Stage {
  return typeof v === 'string' && BY_KEY.has(v as Stage);
}

export function stageTitle(k: Stage): string {
  return BY_KEY.get(k)?.title ?? '';
}

export function isClosed(k: Stage | null | undefined): boolean {
  return !!(k && BY_KEY.get(k)?.closed);
}

/** Воронка: порядок этапов, по которому работает правило «только вперёд» и аналитика. */
export const FUNNEL: readonly Stage[] = [
  'saved',
  'applied',
  'screening',
  'screening_wait',
  'interview',
  'interview_wait',
  'final',
  'final_wait',
  'offer',
];

/** Индекс в воронке; у отказа и «нет ответа» — −1. */
export function funnelIndex(k: Stage | null | undefined): number {
  return k ? FUNNEL.indexOf(k) : -1;
}

/** Самый дальний достигнутый индекс воронки (как `ms()` в прототипе). */
export function reachedIndex(a: { stage: Stage; maxStage?: Stage | null }): number {
  return Math.max(funnelIndex(a.maxStage), funnelIndex(a.stage), a.stage === 'saved' ? 0 : 1);
}

/** Этапы интервью, которые ещё предстоит пройти. */
export const INTERVIEW_STAGES: readonly Stage[] = ['screening', 'interview', 'final'];
/** Этап пройден — ждём итогов от компании. */
export const WAIT_STAGES: readonly Stage[] = ['screening_wait', 'interview_wait', 'final_wait'];
/** Ожидание после этапа: HR-скрининг → «Ждём итогов: HR-скрининг» и т. д. */
export const WAIT_AFTER: Readonly<Partial<Record<Stage, Stage>>> = {
  screening: 'screening_wait',
  interview: 'interview_wait',
  final: 'final_wait',
};

/* ---------- доска ---------- */

/** Колонка на каждый открытый статус: видно, какие этапы пройдены и где ждём итогов. */
export const BOARD_COLUMN_KEYS = [
  'saved',
  'applied',
  'screening',
  'screening_wait',
  'interview',
  'interview_wait',
  'final',
  'final_wait',
  'offer',
] as const satisfies readonly Stage[];
export type BoardColumn = (typeof BOARD_COLUMN_KEYS)[number] | 'archive';

export interface BoardColumnInfo {
  key: (typeof BOARD_COLUMN_KEYS)[number];
  title: string;
  icon: 'bookmark' | 'send' | 'users' | 'calendar' | 'trophy' | 'clock';
  /** Статус колонки — его ставит перетаскивание. */
  stage: Stage;
}

export const BOARD_COLUMNS: readonly BoardColumnInfo[] = [
  { key: 'saved', title: 'Интересно', icon: 'bookmark', stage: 'saved' },
  { key: 'applied', title: 'Отклик', icon: 'send', stage: 'applied' },
  { key: 'screening', title: 'HR-скрининг', icon: 'users', stage: 'screening' },
  { key: 'screening_wait', title: 'Ждём: HR', icon: 'clock', stage: 'screening_wait' },
  { key: 'interview', title: 'Техинтервью', icon: 'calendar', stage: 'interview' },
  { key: 'interview_wait', title: 'Ждём: тех', icon: 'clock', stage: 'interview_wait' },
  { key: 'final', title: 'Финальное', icon: 'calendar', stage: 'final' },
  { key: 'final_wait', title: 'Ждём: финал', icon: 'clock', stage: 'final_wait' },
  { key: 'offer', title: 'Оффер', icon: 'trophy', stage: 'offer' },
];

const COLUMN_OF: Partial<Record<Stage, BoardColumn>> = Object.fromEntries(
  BOARD_COLUMN_KEYS.map((k) => [k, k]),
);

export function columnOf(k: Stage): BoardColumn {
  return COLUMN_OF[k] ?? 'archive';
}

/**
 * Какой статус поставить при переносе карточки в колонку (или в выбранный статус из меню).
 * null — ничего не меняется: статус уже в этой колонке (как `moveTo()` в прототипе).
 */
export function moveTarget(current: Stage, to: Stage): Stage | null {
  if (current === to) return null;
  if (columnOf(current) === columnOf(to) && !isClosed(to)) return null;
  return to;
}
